/**
 * COMPTABILITÉ — VENTES & PRODUITS, LA BILLETTERIE SHOTGUN.
 *
 * Tout ce que les billets et les redditions de comptes permettent de savoir
 * sur la période, sans attendre aucune clôture : les entrées (préventes,
 * guichet, exonérations), les ventes billetterie TTC, ce que Shotgun a
 * retenu, chaque tarif, le rythme des préventes, l'heure d'arrivée, la
 * présence, le guichet par moyen de paiement — et, croisé avec les tickets
 * des bars, ce qu'une entrée dépense au bar.
 *
 * Servi par `GET /api/v1/finance/sales/ticketing` (cœur :
 * `finance/sales-shotgun.ts`), qui exige `finance.revenue.read` ET
 * `finance.live.read` ; sans le second, la section le dit au lieu de
 * s'afficher vide.
 *
 * ═══ CE QUE L'ÉCRAN S'INTERDIT ═══
 *
 *   · la VALEUR FACIALE (prix sans frais) n'est jamais présentée comme un
 *     chiffre d'affaires : les ventes s'appellent « ventes billetterie TTC »,
 *     et leur source (reddition, ou prix + frais des billets) est dite ;
 *   · un montant inconnu s'écrit « — », avec la raison ; jamais 0 ;
 *   · aucun texte coupé : les noms longs de soirées et de tarifs reviennent à
 *     la ligne.
 *
 * ═══ LES GRAPHIQUES ═══
 *
 * Maison, une seule série chacun, dans l'accent (la teinte de données de la
 * page) : des colonnes fines (24 px au plus, sommet arrondi, 2 px d'air entre
 * elles), une ligne de lecture au-dessus qui dit la valeur survolée — ou la
 * plus forte —, la valeur de la plus forte écrite sur sa colonne et nulle
 * part ailleurs, et la vue « tableau » de chacun. Le cumul des préventes
 * n'est PAS tracé sur un second axe : il est dans la ligne de lecture et dans
 * le tableau.
 */

import { useState, type CSSProperties, type ReactNode } from 'react'
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, Banknote, BarChart3, Clock, CreditCard, DoorOpen, Martini, Percent, Receipt,
  RefreshCcw, ScanLine, Tags, Ticket, Users,
} from 'lucide-react'
import { Link } from '../router.js'
import { SegmentedControl } from '../admin/pickers.js'
import { formatMoney } from './money.js'
import { EnCours, Indisponible, nuitCourte } from './ClosingCommon.js'
import { useMenuSoiree } from '../soirees/menu-soiree.js'

/* ═══════════════════════ les formes lues (miroir du cœur, `finance/sales-shotgun.ts`) ═══════════════════════ */

interface KpisBilletterie {
  soirees: number; valid: number; presale_paid: number; onsite_paid: number; free: number; unpriced: number
  scanned: number; presale_valid: number; presale_scanned: number
  presence_rate: number | null; presale_presence_rate: number | null
  resold: number; refunded: number
  ttc_cents: number | null; online_ttc_cents: number | null; onsite_ttc_cents: number | null
  facial_cents: number; spend_per_entry_cents: number | null; ticketing_share: number | null
}

export interface Billetterie {
  period: { from: string; to: string; days: number; preset: string | null }
  previous: { from: string; to: string; has_data: boolean }
  has_data: boolean
  kpis: KpisBilletterie
  previous_kpis: KpisBilletterie | null
  sources: { reddition: number; tickets: number; unknown: number; unknown_soirees: string[] }
  reddition: {
    soirees: number; of_soirees: number
    reports: Array<{ report_id: string | null; business_date: string; event_name: string | null }>
    sales_ttc_cents: number | null; online_ttc_cents: number | null; onsite_ttc_cents: number | null
    fees_ttc_cents: number | null; commissions_ttc_cents: number | null; discounts_ttc_cents: number | null
    /** Frais de surclassement de billet ; absent d'un serveur antérieur. */
    upgrades_ttc_cents?: number | null
    collected_online_ttc_cents: number | null; balance_cents: number | null; revenue_cents: number | null
    tickets: { issued: number | null; refunded: number | null; resold: number | null; final: number | null }
  } | null
  deals: Array<{
    label: string; channel: string; issued: number | null; refunded: number | null; resold: number | null; final: number
    public_cents: number | null; revenue_cents: number | null; commission_cents: number | null
    price_range_cents: { min: number; max: number } | null
    sources: string[]; soirees: number
  }>
  presale_pace: { days: Array<{ day: number; tickets: number; cumulative: number }>; before: number; total: number; half_by_day: number | null }
  arrivals: { slots: Array<{ minute: number; label: string; scans: number }>; peak: { label: string; scans: number } | null; half_by: string | null; outside: number }
  onsite_payments: Array<{ method: string; tickets: number; cents: number; share: number | null }>
  cross: {
    spend_nights: number; entries: number; bar_cents: number; spend_per_entry_cents: number | null
    share_nights: number; ticketing_ttc_cents: number; zap_cents: number; ticketing_share: number | null
    excluded: Array<{ business_date: string; reason: string }>
  }
  by_night: Array<{
    business_date: string; weekday: number; soirees: string[]; valid: number; scanned: number
    presale_paid: number; onsite_paid: number; free: number
    ttc_cents: number | null; ttc_source: 'REDDITION' | 'BILLETS' | 'MIXTE' | null
    bar_cents: number | null; spend_per_entry_cents: number | null
  }>
}

const objet = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null

/** Garde minimale : on vérifie l'identité de la forme, pas chaque champ. */
export const commeBilletterie = (v: unknown): Billetterie | null => {
  const o = objet(v)
  return o && objet(o['kpis']) && Array.isArray(o['deals']) && objet(o['presale_pace']) && objet(o['cross']) ? (o as unknown as Billetterie) : null
}

/** L'état de la lecture, posé par la page (qui porte la période). */
export type LectureBilletterie =
  | { etat: 'interdit' }
  | { etat: 'chargement' }
  | { etat: 'erreur'; titre: string; message: string }
  | { etat: 'ok'; valeur: Billetterie; rechargement: boolean }

/* ═══════════════════════ petits outils d'écriture ═══════════════════════ */

const montant = (c: number | null): string => formatMoney(c).replace(/ €$/, ' €')
const nombre = (n: number): string => n.toLocaleString('fr-FR')
const decimal = (n: number): string => n.toLocaleString('fr-FR', { maximumFractionDigits: 1 })
const pluriel = (n: number, un: string, plusieurs = `${un}s`): string => `${nombre(n)} ${n > 1 ? plusieurs : un}`
const compte = (n: number | null): string => (n === null ? '—' : nombre(n))
const pourcent = (p: number | null): string =>
  p === null ? '—' : `${(p * 100).toLocaleString('fr-FR', { maximumFractionDigits: p < 0.1 || p > 0.9 ? 1 : 0 })} %`
/** « J−3 », « J0 » : le signe moins typographique. */
const jour = (j: number): string => (j === 0 ? 'J0' : `J−${Math.abs(j)}`)

const METHODES: Record<string, string> = {
  card_present: 'Carte au TPE', cash: 'Espèces', other: 'Autres moyens (carte en ligne, portefeuille)', unknown: 'Moyen non relevé',
}
const SOURCE_TTC: Record<string, string> = {
  REDDITION: 'd’après la reddition', BILLETS: 'prix + frais des billets', MIXTE: 'reddition et billets',
}

/** L'évolution d'un indicateur contre la période précédente — signe écrit, jamais la couleur seule. */
function Delta(props: { actuel: number | null; avant: number | null | undefined; contre: string }) {
  const { actuel, avant } = props
  if (actuel === null || avant === null || avant === undefined || avant <= 0) return null
  const pct = Math.round(((actuel - avant) / avant) * 100)
  const sens = pct > 0 ? 'hausse' : pct < 0 ? 'baisse' : 'stable'
  return (
    <span className={`fsv-delta fsv-delta--${sens}`}>
      {sens === 'hausse' ? <ArrowUpRight size={14} aria-hidden="true" /> : sens === 'baisse' ? <ArrowDownRight size={14} aria-hidden="true" /> : null}
      <strong>{pct > 0 ? '+' : pct < 0 ? '−' : ''}{nombre(Math.abs(pct))}&nbsp;%</strong> par rapport {props.contre}
    </span>
  )
}

function Tuile(props: { icone: ReactNode; label: string; valeur: string; sous?: ReactNode; delta?: ReactNode; accent?: boolean }) {
  return (
    <div className={`fsv-tuile${props.accent ? ' fsv-tuile--accent' : ''}`}>
      <span className="fsv-tuile__label"><span aria-hidden="true">{props.icone}</span>{props.label}</span>
      <span className="fsv-tuile__valeur">{props.valeur}</span>
      {props.sous ? <span className="fsv-tuile__sous">{props.sous}</span> : null}
      {props.delta ?? null}
    </div>
  )
}

function Tete(props: { icone: ReactNode; titre: string; id: string; children?: ReactNode }) {
  return (
    <div className="lc-panel__head fsv-tete">
      <span className="fsv-tete__icone" aria-hidden="true">{props.icone}</span>
      <h3 className="lc-panel__title fsv-tete__titre" id={props.id}>{props.titre}</h3>
      {props.children ? <div className="fsv-tete__outils">{props.children}</div> : null}
    </div>
  )
}

/* ═══════════════════════════════ la section ═══════════════════════════════ */

export function SectionBilletterie(props: {
  lecture: LectureBilletterie
  /** « au sam. 26 sept. », « aux 7 nuits d’avant » — accordé par la page (`libelleAvant`). */
  contre: string
  /** « le sam. 26 sept. », « sur les 7 nuits d’avant ». */
  contreSur: string
}) {
  const l = props.lecture
  return (
    <section className="fsv-partie" aria-labelledby="fsb-titre">
      <header className="fsv-partie__tete">
        <span className="fsv-partie__icone" aria-hidden="true"><Ticket size={22} /></span>
        <div className="fsv-partie__texte">
          <h2 className="fsv-partie__titre" id="fsb-titre">La billetterie Shotgun</h2>
          <p className="fsv-partie__sous">
            D’après les billets et les redditions de comptes de Shotgun : les entrées, les ventes, le guichet et le bar par entrée.
            Rien ici n’attend une clôture.
          </p>
        </div>
      </header>

      {l.etat === 'interdit' ? (
        <section className="lc-panel">
          <Indisponible titre="La billetterie n’est pas accessible"
            message="Votre rôle permet de lire les ventes des caisses, pas celles de Shotgun : la billetterie demande en plus le droit de consulter les ventes en direct." />
        </section>
      ) : null}
      {l.etat === 'chargement' ? <EnCours quoi="de la billetterie" /> : null}
      {l.etat === 'erreur' ? <section className="lc-panel"><Indisponible titre={l.titre} message={l.message} /></section> : null}
      {l.etat === 'ok' ? (
        <div className={`fsv-partie__corps${l.rechargement ? ' fsv-contenu--recharge' : ''}`} aria-busy={l.rechargement}>
          {!l.valeur.has_data ? (
            <section className="lc-panel">
              <div className="fi-empty">
                <div className="fi-empty__title">Aucun billet valide sur cette période</div>
                <p className="fsv-vide__texte">Aucune soirée de ces nuits n’a de billet valide dans Shotgun : il n’y a ni entrée, ni vente de billetterie à lire.</p>
              </div>
            </section>
          ) : <Contenu b={l.valeur} contre={props.contre} contreSur={props.contreSur} />}
        </div>
      ) : null}
    </section>
  )
}

function Contenu(props: { b: Billetterie; contre: string; contreSur: string }) {
  const { b } = props
  return (
    <>
      <Indicateurs b={b} contre={props.contre} contreSur={props.contreSur} />
      <div className="fsv-grille fsv-grille--deux">
        <Ventes b={b} />
        <Guichet b={b} />
      </div>
      <Tarifs b={b} />
      <div className="fsv-grille fsv-grille--deux">
        <Preventes b={b} />
        <Arrivees b={b} />
      </div>
      <NuitParNuit b={b} />
    </>
  )
}

/* ═══════════════════════════════ l'essentiel ═══════════════════════════════ */

function Indicateurs(props: { b: Billetterie; contre: string; contreSur: string }) {
  const { b } = props
  const k = b.kpis
  const p = b.previous_kpis
  const c = b.cross
  const sourceVentes = k.ttc_cents === null
    ? `inconnues pour ${pluriel(b.sources.unknown, 'soirée')} : ni reddition, ni frais de service relevés`
    : b.sources.reddition > 0 && b.sources.tickets === 0
      ? `d’après ${b.sources.reddition > 1 ? `les redditions des ${nombre(b.sources.reddition)} soirées` : 'la reddition de la soirée'}`
      : b.sources.reddition > 0
        ? `${pluriel(b.sources.reddition, 'soirée')} d’après sa reddition, ${pluriel(b.sources.tickets, 'autre')} d’après les billets (prix + frais)`
        : 'prix + frais de service des billets valides : aucune reddition reçue'
  return (
    <section className="fsv-tuiles" aria-label="L’essentiel de la billetterie">
      <Tuile accent icone={<Users size={15} />} label="Entrées" valeur={nombre(k.valid)}
        sous={`billets valides : ${nombre(k.presale_paid)} en prévente, ${nombre(k.onsite_paid)} au guichet, ${nombre(k.free)} exonérés${k.unpriced > 0 ? `, ${nombre(k.unpriced)} sans prix` : ''}`}
        delta={<Delta actuel={k.valid} avant={p?.valid} contre={props.contre} />} />
      <Tuile icone={<Receipt size={15} />} label="Ventes billetterie TTC" valeur={montant(k.ttc_cents)} sous={sourceVentes}
        delta={<Delta actuel={k.ttc_cents} avant={p?.ttc_cents} contre={props.contre} />} />
      <Tuile icone={<ScanLine size={15} />} label="Présence" valeur={pourcent(k.presence_rate)}
        sous={`${nombre(k.scanned)} billets scannés sur ${nombre(k.valid)} · ${pourcent(k.presale_presence_rate)} des préventes${p && p.presence_rate !== null ? ` · ${pourcent(p.presence_rate)} ${props.contreSur}` : ''}`} />
      <Tuile icone={<Martini size={15} />} label="Dépense au bar par entrée" valeur={montant(c.spend_per_entry_cents)}
        sous={c.spend_nights > 0
          ? `encaissé des bars ÷ entrées, sur ${pluriel(c.spend_nights, 'nuit')} où tous les bars sont lus`
          : 'aucune nuit de la période n’a tous ses bars lus'}
        delta={<Delta actuel={c.spend_per_entry_cents} avant={p?.spend_per_entry_cents} contre={props.contre} />} />
      <Tuile icone={<Percent size={15} />} label="Part de la billetterie" valeur={pourcent(c.ticketing_share)}
        sous={c.share_nights > 0
          ? `des ventes TTC billetterie et caisses ZAP réunies, sur ${pluriel(c.share_nights, 'nuit')}`
          : 'aucune nuit n’a à la fois ses ventes de billetterie et toutes ses caisses lues'} />
      <Tuile icone={<RefreshCcw size={15} />} label="Revendus et remboursés" valeur={`${nombre(k.resold)} · ${nombre(k.refunded)}`}
        sous={`${pluriel(k.resold, 'billet revendu', 'billets revendus')} sur la bourse Shotgun, ${pluriel(k.refunded, 'remboursé', 'remboursés')}`} />
    </section>
  )
}

/* ═══════════════════════════════ ce que la billetterie a vendu ═══════════════════════════════ */

function Ventes(props: { b: Billetterie }) {
  const { b } = props
  const k = b.kpis
  const r = b.reddition
  const lignes = [
    { cle: 'en-ligne', nom: 'Préventes en ligne', sous: 'prix et frais de service payés par les acheteurs', cents: k.online_ttc_cents },
    { cle: 'guichet', nom: 'Guichet, le soir même', sous: 'aucun frais de service au guichet', cents: k.onsite_ttc_cents },
  ]
  const max = Math.max(1, ...lignes.map((x) => x.cents ?? 0))
  const total = k.ttc_cents
  return (
    <section className="lc-panel fsv-paiements" aria-labelledby="fsb-ventes-titre">
      <Tete icone={<Receipt size={17} />} titre="Ce que la billetterie a vendu" id="fsb-ventes-titre">
        <span className="fsv-tete__total fi-money">{montant(total)}</span>
      </Tete>
      <div className="lc-panel__body fsb-ventes">
        <ul className="fsv-barres">
          {lignes.map((x) => (
            <li key={x.cle} className="fsv-barre-ligne">
              <span className="fsv-barre-ligne__nom">
                {x.nom}
                <span className="fsv-barre-ligne__sous">{x.sous}{total !== null && x.cents !== null && total > 0 ? ` · ${pourcent(x.cents / total)} des ventes` : ''}</span>
              </span>
              <span className="fsv-barre-ligne__piste" aria-hidden="true">
                {x.cents !== null ? <span className="fsv-barre-ligne__barre" style={{ width: `${Math.max(2, Math.round((x.cents / max) * 100))}%` }} /> : null}
              </span>
              <span className="fsv-barre-ligne__val fi-money">{montant(x.cents)}</span>
            </li>
          ))}
        </ul>
        {b.sources.unknown > 0 ? (
          <p className="fi-reserve fsb-reserve">
            Ventes inconnues pour {pluriel(b.sources.unknown, 'soirée')} — {b.sources.unknown_soirees.join(' · ')}
            {b.sources.unknown > b.sources.unknown_soirees.length ? `, et ${nombre(b.sources.unknown - b.sources.unknown_soirees.length)} autres` : ''} :
            sans reddition, et avec des frais de service non relevés (la synchro ne les lit que depuis le 1er septembre 2026), leur total ne peut pas être affirmé.
          </p>
        ) : null}

        <div className="fsb-reddition">
          <h4 className="fsv-bloc__titre">
            {r ? `Selon ${r.soirees > 1 ? `les redditions de ${nombre(r.soirees)} soirées` : 'la reddition de la soirée'}${r.of_soirees > r.soirees ? ` sur ${nombre(r.of_soirees)}` : ''}` : 'Selon les redditions de comptes'}
          </h4>
          {!r ? (
            <p className="lc-hint">Aucune reddition de comptes Shotgun lue pour ces nuits : les commissions, remises et le solde reversé ne sont connus que par elle.</p>
          ) : (
            <>
              <dl className="fsb-dl">
                <div><dt>Ventes TTC</dt><dd className="fi-money">{montant(r.sales_ttc_cents)}</dd></div>
                <div><dt>Collecté en ligne par Shotgun</dt><dd className="fi-money">{montant(r.collected_online_ttc_cents)}</dd></div>
                <div><dt>Commissions Shotgun</dt><dd className="fi-money">{montant(r.commissions_ttc_cents)}</dd></div>
                <div><dt>Remises commerciales</dt><dd className="fi-money">{montant(r.discounts_ttc_cents)}</dd></div>
                {/* Une ligne à part chez Shotgun : 30 % de la différence quand un
                    billet revendu passe dans une catégorie supérieure. Montrée
                    dès qu'elle existe — ou qu'elle est inconnue (« — »). */}
                {r.upgrades_ttc_cents !== undefined && r.upgrades_ttc_cents !== 0 ? (
                  <div><dt>Frais de surclassement de billet</dt><dd className="fi-money">{montant(r.upgrades_ttc_cents)}</dd></div>
                ) : null}
                <div><dt>Frais facturés par Shotgun</dt><dd className="fi-money">{montant(r.fees_ttc_cents)}</dd></div>
                <div className="fsb-dl__fort"><dt>Solde reversé</dt><dd className="fi-money">{montant(r.balance_cents)}</dd></div>
                <div><dt>Part de l’organisateur, tous tarifs</dt><dd className="fi-money">{montant(r.revenue_cents)}</dd></div>
                <div>
                  <dt>Billets émis, revendus, remboursés</dt>
                  <dd>{compte(r.tickets.issued)} · {compte(r.tickets.resold)} · {compte(r.tickets.refunded)}</dd>
                </div>
              </dl>
              <p className="lc-hint fsb-note">
                {r.reports.map((x) => `${x.report_id ?? 'reddition sans référence'} (${nuitCourte(x.business_date).toLocaleLowerCase('fr')})`).join(' · ')}
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  )
}

/* ═══════════════════════════════ le guichet ═══════════════════════════════ */

function Guichet(props: { b: Billetterie }) {
  const { b } = props
  const m = b.onsite_payments
  const max = Math.max(1, ...m.map((x) => x.cents))
  const icone = (k: string): ReactNode => (k === 'cash' ? <Banknote size={15} aria-hidden="true" /> : k === 'unknown' ? <AlertTriangle size={15} aria-hidden="true" /> : <CreditCard size={15} aria-hidden="true" />)
  return (
    <section className="lc-panel fsv-paiements" aria-labelledby="fsb-guichet-titre">
      <Tete icone={<DoorOpen size={17} />} titre="Le guichet, par moyen de paiement" id="fsb-guichet-titre">
        <span className="lc-hint">{pluriel(b.kpis.onsite_paid, 'billet payant', 'billets payants')}</span>
      </Tete>
      <div className="lc-panel__body">
        {m.length === 0 ? <p className="lc-hint">Aucun billet payant vendu au guichet sur la période.</p> : (
          <ul className="fsv-barres">
            {m.map((x) => (
              <li key={x.method} className={`fsv-barre-ligne${x.method === 'unknown' ? ' fsv-barre-ligne--hors' : ''}`}>
                <span className="fsv-barre-ligne__nom">
                  <span className="fsb-moyen">{icone(x.method)}{METHODES[x.method] ?? x.method}</span>
                  <span className="fsv-barre-ligne__sous">{pourcent(x.share)} du guichet · {pluriel(x.tickets, 'billet')}</span>
                </span>
                <span className="fsv-barre-ligne__piste" aria-hidden="true"><span className="fsv-barre-ligne__barre" style={{ width: `${Math.max(2, Math.round((x.cents / max) * 100))}%` }} /></span>
                <span className="fsv-barre-ligne__val fi-money">{montant(x.cents)}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="lc-hint fsb-note">
          {b.kpis.free > 0 ? `Et ${pluriel(b.kpis.free, 'billet exonéré', 'billets exonérés')} à 0 €, rien à encaisser. ` : ''}
          Ces euros sont ceux de la caisse de l’accueil : ils s’y comparent, ils ne s’y ajoutent pas.
        </p>
      </div>
    </section>
  )
}

/* ═══════════════════════════════ les tarifs ═══════════════════════════════ */

function Tarifs(props: { b: Billetterie }) {
  const { b } = props
  return (
    <section className="lc-panel" aria-labelledby="fsb-tarifs-titre">
      <Tete icone={<Tags size={17} />} titre="Tarif par tarif" id="fsb-tarifs-titre">
        <span className="lc-hint">{pluriel(b.deals.length, 'tarif')} · la reddition quand elle existe, sinon les billets</span>
      </Tete>
      <div className="lc-panel__body">
        {b.deals.length === 0 ? <p className="lc-hint">Aucun tarif relevé sur la période.</p> : (
          <div className="fi-tablewrap">
            <table className="fi-table fsb-table">
              <thead>
                <tr>
                  <th scope="col">Tarif</th>
                  <th scope="col" className="fi-money">Prix d’un billet</th>
                  <th scope="col" className="fi-money">Émis</th>
                  <th scope="col" className="fi-money">Revendus</th>
                  <th scope="col" className="fi-money">Remboursés</th>
                  <th scope="col" className="fi-money">Valides</th>
                  <th scope="col" className="fi-money">Payé par les acheteurs</th>
                  <th scope="col" className="fi-money">Dont frais Shotgun</th>
                  <th scope="col" className="fi-money">Part de l’organisateur</th>
                </tr>
              </thead>
              <tbody>
                {b.deals.map((d) => (
                  <tr key={`${d.channel}-${d.label}`}>
                    <th scope="row">
                      <span className="fi-cell-titre">{d.label}</span>
                      <span className="fi-cell-sous">
                        {d.channel === 'onsite' ? 'guichet' : 'en ligne'}
                        {d.soirees > 1 ? ` · ${nombre(d.soirees)} soirées` : ''}
                        {' · '}{d.sources.length > 1 ? 'reddition et billets' : d.sources[0] === 'REDDITION' ? 'reddition' : 'billets'}
                      </span>
                    </th>
                    <td className="fi-money">
                      {d.price_range_cents === null
                        ? d.revenue_cents === 0 ? 'gratuit' : '—'
                        : d.price_range_cents.min === d.price_range_cents.max
                          ? montant(d.price_range_cents.min)
                          : `de ${montant(d.price_range_cents.min)} à ${montant(d.price_range_cents.max)}`}
                    </td>
                    <td className="fi-money">{compte(d.issued)}</td>
                    <td className="fi-money">{compte(d.resold)}</td>
                    <td className="fi-money">{compte(d.refunded)}</td>
                    <td className="fi-money fi-money--fort">{nombre(d.final)}</td>
                    <td className="fi-money">{montant(d.public_cents)}</td>
                    <td className="fi-money">{montant(d.commission_cents)}</td>
                    <td className="fi-money fi-money--fort">{montant(d.revenue_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="lc-hint fsb-note">
          « Payé par les acheteurs » = prix + frais de service : c’est la vente TTC. « Part de l’organisateur » = le prix des billets, avant les frais que Shotgun facture.
          {b.deals.some((d) => d.sources.includes('BILLETS') && (d.resold ?? 0) > 0) ? ' Les reventes lues dans les billets comptent chaque passage par la bourse : la reddition, quand elle arrive, fait foi.' : ''}
        </p>
      </div>
    </section>
  )
}

/* ═══════════════════════════════ les colonnes (graphique maison) ═══════════════════════════════ */

/** `tick` vide : pas d'étiquette sous cette colonne ; `secondaire` : étiquette effacée sur un téléphone. */
interface Point { cle: string; tick: string; valeur: number; lecture: ReactNode; aria: string; secondaire?: boolean }

/**
 * Des COLONNES, une série : la lecture au-dessus (la colonne survolée ou
 * touchée, sinon la plus forte), la valeur de la plus forte sur son sommet,
 * une étiquette d'axe tous les `pas` points. Le survol ET le focus clavier
 * lisent la même chose.
 */
function Colonnes(props: { points: Point[]; pas: number; ecrire: (v: number) => string; aide: string; label: string; dernier?: boolean }) {
  const { points } = props
  const [vise, setVise] = useState<number | null>(null)
  const max = Math.max(0, ...points.map((p) => p.valeur))
  let pic: number | null = null
  points.forEach((p, i) => { if (p.valeur > 0 && (pic === null || p.valeur > points[pic]!.valeur)) pic = i })
  const lu = vise ?? pic
  const pl = lu !== null ? points[lu] : undefined
  const n = points.length
  return (
    <div className="fsb-cols" style={{ '--fsb-n': n } as CSSProperties}>
      <p className="fsv-lecture" aria-live="polite">
        {pl ? <>{pl.lecture}{vise === null ? <span className="fsv-lecture__aide">{props.aide}</span> : null}</> : <span className="fsv-lecture__aide">Rien à tracer sur la période.</span>}
      </p>
      <div className="fsb-cols__plot" role="group" aria-label={props.label} onMouseLeave={() => setVise(null)}>
        {points.map((p, i) => {
          const pct = max === 0 ? 0 : Math.max(p.valeur > 0 ? 2 : 0, Math.round((p.valeur / max) * 100))
          return (
            <button key={p.cle} type="button" className={`fsb-cols__col${lu === i ? ' fsb-cols__col--vise' : ''}`} aria-label={p.aria}
              onMouseEnter={() => setVise(i)} onFocus={() => setVise(i)} onBlur={() => setVise(null)}>
              {i === pic ? (
                <span className={`fsb-cols__valeur${i < 2 ? ' fsb-cols__valeur--debut' : i > n - 3 ? ' fsb-cols__valeur--fin' : ''}`} style={{ bottom: `${pct}%` }}>{props.ecrire(p.valeur)}</span>
              ) : null}
              <span className="fsb-cols__barre" style={{ height: `${pct}%` }} />
            </button>
          )
        })}
      </div>
      <div className="fsb-cols__axe" aria-hidden="true">
        {/* La dernière étiquette n'est forcée que si elle compte (J0) : sinon elle déborderait du tracé. */}
        {points.map((p, i) => (
          <span key={p.cle} className={i % props.pas === 0 || (props.dernier && i === n - 1) ? (p.secondaire ? 'fsb-cols__tick--secondaire' : undefined) : 'fsb-cols__tick--muet'}>{p.tick}</span>
        ))}
      </div>
    </div>
  )
}

/** Un pas d'étiquettes qui laisse respirer l'axe : une sur deux au-delà de 12 points, une sur quatre au-delà de 24. */
const pasPour = (n: number): number => (n > 36 ? 6 : n > 24 ? 4 : n > 12 ? 2 : 1)

/* ═══════════════════════════════ le rythme des préventes ═══════════════════════════════ */

function Preventes(props: { b: Billetterie }) {
  const { b } = props
  const r = b.presale_pace
  const [rendu, setRendu] = useState<'graphique' | 'tableau'>('graphique')
  /* De la première vente (J−30 au plus loin) à J0, au moins la dernière
     semaine : une prévente vendue la veille ne fait pas un graphique d'un trait. */
  const premier = r.days.find((d) => d.tickets > 0)?.day ?? 0
  const jours = r.days.filter((d) => d.day >= Math.min(premier, -7))
  const points: Point[] = jours.map((d) => {
    const cumul = r.total > 0 ? d.cumulative / r.total : null
    return {
      cle: String(d.day), tick: jour(d.day), valeur: d.tickets,
      lecture: <><strong>{jour(d.day)}</strong><span>{pluriel(d.tickets, 'prévente')}</span><span className="fsv-lecture__avant">{pourcent(cumul)} des préventes vendues à ce stade</span></>,
      aria: `${jour(d.day)} : ${pluriel(d.tickets, 'prévente')}, ${pourcent(cumul)} des préventes vendues à ce stade`,
    }
  })
  return (
    <section className="lc-panel" aria-labelledby="fsb-preventes-titre">
      <Tete icone={<BarChart3 size={17} />} titre="Le rythme des préventes" id="fsb-preventes-titre">
        <div className="fsv-seg">
          <SegmentedControl value={rendu} onChange={setRendu} size="sm" ariaLabel="Présentation du rythme des préventes"
            options={[{ value: 'graphique', label: 'Graphique' }, { value: 'tableau', label: 'Tableau' }]} />
        </div>
      </Tete>
      <div className="lc-panel__body">
        <p className="fsb-resume">
          {r.total === 0 ? 'Aucune prévente horodatée sur la période.' : (
            <>
              {pluriel(r.total, 'prévente valide', 'préventes valides')}, jour par jour avant leur soirée{b.kpis.soirees > 1 ? ` (${nombre(b.kpis.soirees)} soirées réunies)` : ''}.{' '}
              {r.half_by_day === null ? null : r.half_by_day < -30
                ? <>La moitié était vendue <strong>plus de 30 jours avant</strong>.</>
                : r.half_by_day === 0
                  ? <>Plus de la moitié se vend <strong>le jour même</strong>.</>
                  : <>La moitié est vendue <strong>à {jour(r.half_by_day)}</strong>.</>}
              {r.before > 0 ? ` ${pluriel(r.before, 'prévente vendue', 'préventes vendues')} plus de 30 jours avant n’apparaissent pas sur le graphique, mais comptent dans le cumul.` : ''}
            </>
          )}
        </p>
        {r.total === 0 ? null : rendu === 'tableau' ? (
          <div className="fi-tablewrap">
            <table className="fi-table fsv-table fsb-table--court">
              <thead><tr><th scope="col">Jour</th><th scope="col" className="fi-money">Préventes</th><th scope="col" className="fi-money">Cumul</th><th scope="col" className="fi-money">Part vendue</th></tr></thead>
              <tbody>
                {jours.map((d) => (
                  <tr key={d.day}>
                    <th scope="row">{jour(d.day)}</th>
                    <td className="fi-money">{nombre(d.tickets)}</td>
                    <td className="fi-money">{nombre(d.cumulative)}</td>
                    <td className="fi-money">{pourcent(r.total > 0 ? d.cumulative / r.total : null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Colonnes dernier points={points} pas={pasPour(points.length)} ecrire={(v) => nombre(v)} label="Préventes par jour avant la soirée"
            aide="le jour le plus fort — survolez une colonne pour lire les autres" />
        )}
      </div>
    </section>
  )
}

/* ═══════════════════════════════ les arrivées ═══════════════════════════════ */

function Arrivees(props: { b: Billetterie }) {
  const { b } = props
  const a = b.arrivals
  const [pasHoraire, setPasHoraire] = useState<'quart' | 'heure'>('quart')
  const [rendu, setRendu] = useState<'graphique' | 'tableau'>('graphique')
  const soirees = Math.max(1, b.kpis.soirees)
  /* Par heure : les quatre quarts d'une heure réunis, dans l'ordre de la nuit. */
  const creneaux = pasHoraire === 'quart'
    ? a.slots.map((s) => ({ cle: String(s.minute), debut: s.label, fin: libelleMinute((s.minute + 15) % 1440), scans: s.scans }))
    : regrouperParHeure(a.slots)
  const lecture = (c: { debut: string; fin: string; scans: number }): string =>
    `${pluriel(c.scans, 'entrée scannée', 'entrées scannées')}${soirees > 1 ? `, ${decimal(c.scans / soirees)} par soirée en moyenne` : ''}`
  /* Les étiquettes de l'axe : les heures pleines seulement (« 23 h », « 00 h »),
     une sur deux effacée sur un téléphone — « 22 h 30 » sous chaque quart
     d'heure se chevauchait dès 300 px. */
  let heuresVues = 0
  const points: Point[] = creneaux.map((c) => {
    const pleine = /h 00$/.test(c.debut) || /^\d\d h$/.test(c.debut)
    const tick = pleine ? c.debut.replace(/ 00$/, '') : ''
    const secondaire = pleine ? heuresVues++ % 2 === 1 : false
    return {
    cle: c.cle, tick, secondaire, valeur: c.scans,
    lecture: <><strong>{c.debut} – {c.fin}</strong><span>{lecture(c)}</span></>,
    aria: `De ${c.debut} à ${c.fin} : ${lecture(c)}`,
    }
  })
  return (
    <section className="lc-panel" aria-labelledby="fsb-arrivees-titre">
      <Tete icone={<Clock size={17} />} titre="L’heure d’arrivée" id="fsb-arrivees-titre">
        <div className="fsv-seg">
          <SegmentedControl value={pasHoraire} onChange={setPasHoraire} size="sm" ariaLabel="Pas des arrivées"
            options={[{ value: 'quart', label: 'Par quart d’heure' }, { value: 'heure', label: 'Par heure' }]} />
          <SegmentedControl value={rendu} onChange={setRendu} size="sm" ariaLabel="Présentation des arrivées"
            options={[{ value: 'graphique', label: 'Graphique' }, { value: 'tableau', label: 'Tableau' }]} />
        </div>
      </Tete>
      <div className="lc-panel__body">
        <p className="fsb-resume">
          {a.slots.length === 0 ? 'Aucun billet scanné entre 18 h et 6 h sur la période.' : (
            <>
              L’heure où chaque billet est passé au lecteur{soirees > 1 ? `, ${nombre(soirees)} soirées réunies` : ''}.{' '}
              {a.peak ? <>Le quart d’heure le plus chargé commence à <strong>{a.peak.label}</strong>. </> : null}
              {a.half_by ? <>La moitié des entrées est passée <strong>avant {a.half_by}</strong>.</> : null}
            </>
          )}
          {a.outside > 0 ? ` ${pluriel(a.outside, 'billet porte', 'billets portent')} une heure de scan entre 6 h et 18 h, porte fermée : c’est l’heure où le guichet a transmis ses ventes à Shotgun, pas celle de l’arrivée — ${a.outside > 1 ? 'ils sont' : 'il est'} hors du graphique.` : ''}
        </p>
        {a.slots.length === 0 ? null : rendu === 'tableau' ? (
          <div className="fi-tablewrap">
            <table className="fi-table fsv-table fsb-table--court">
              <thead><tr><th scope="col">Créneau</th><th scope="col" className="fi-money">Entrées scannées</th>{soirees > 1 ? <th scope="col" className="fi-money">Par soirée</th> : null}</tr></thead>
              <tbody>
                {creneaux.map((c) => (
                  <tr key={c.cle}>
                    <th scope="row">{c.debut} – {c.fin}</th>
                    <td className="fi-money">{nombre(c.scans)}</td>
                    {soirees > 1 ? <td className="fi-money">{decimal(c.scans / soirees)}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Colonnes points={points} pas={1} ecrire={(v) => nombre(v)} label="Entrées scannées par créneau de la nuit"
            aide="le créneau le plus chargé — survolez une colonne pour lire les autres" />
        )}
      </div>
    </section>
  )
}

const libelleMinute = (m: number): string => `${String(Math.floor(m / 60) % 24).padStart(2, '0')} h ${String(m % 60).padStart(2, '0')}`

function regrouperParHeure(slots: Billetterie['arrivals']['slots']): Array<{ cle: string; debut: string; fin: string; scans: number }> {
  const out: Array<{ cle: string; debut: string; fin: string; scans: number }> = []
  for (const s of slots) {
    const h = Math.floor(s.minute / 60)
    const debut = `${String(h).padStart(2, '0')} h`
    const der = out[out.length - 1]
    if (der && der.cle === String(h)) der.scans += s.scans
    else out.push({ cle: String(h), debut, fin: `${String((h + 1) % 24).padStart(2, '0')} h`, scans: s.scans })
  }
  return out
}

/* ═══════════════════════════════ nuit par nuit ═══════════════════════════════ */

function NuitParNuit(props: { b: Billetterie }) {
  const { b } = props
  const menuSoiree = useMenuSoiree()
  const exclues = b.cross.excluded
  return (
    <section className="lc-panel" aria-labelledby="fsb-nuits-titre">
      <Tete icone={<Ticket size={17} />} titre="Nuit par nuit : les entrées et le bar" id="fsb-nuits-titre">
        <span className="lc-hint">{pluriel(b.by_night.length, 'nuit')} avec des billets valides</span>
      </Tete>
      <div className="lc-panel__body">
        <div className="fi-tablewrap">
          <table className="fi-table fsb-table">
            <thead>
              <tr>
                <th scope="col">Nuit</th>
                <th scope="col" className="fi-money">Entrées</th>
                <th scope="col" className="fi-money">Présence</th>
                <th scope="col" className="fi-money">Préventes</th>
                <th scope="col" className="fi-money">Guichet</th>
                <th scope="col" className="fi-money">Exonérés</th>
                <th scope="col" className="fi-money">Ventes billetterie TTC</th>
                <th scope="col" className="fi-money">Encaissé des bars</th>
                <th scope="col" className="fi-money">Bar par entrée</th>
              </tr>
            </thead>
            <tbody>
              {b.by_night.map((n) => (
                <tr key={n.business_date}>
                  <th scope="row">
                    <Link href={`/comptabilite/nuits/${n.business_date}`} className="fi-cell-titre" {...menuSoiree.nuit({ date: n.business_date })}>{nuitCourte(n.business_date)}</Link>
                    <span className="fi-cell-sous">{n.soirees.join(' · ')}</span>
                  </th>
                  <td className="fi-money fi-money--fort">{nombre(n.valid)}</td>
                  <td className="fi-money">{pourcent(n.valid > 0 ? n.scanned / n.valid : null)}</td>
                  <td className="fi-money">{nombre(n.presale_paid)}</td>
                  <td className="fi-money">{nombre(n.onsite_paid)}</td>
                  <td className="fi-money">{nombre(n.free)}</td>
                  <td className="fi-money">
                    {montant(n.ttc_cents)}
                    <span className="fi-cell-sous">{n.ttc_source ? SOURCE_TTC[n.ttc_source] : 'frais non relevés'}</span>
                  </td>
                  <td className="fi-money">{n.bar_cents === null ? '—' : montant(n.bar_cents)}</td>
                  <td className="fi-money fi-money--fort">{montant(n.spend_per_entry_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {exclues.length > 0 ? (
          <details className="fsv-couv__detail fsb-exclues">
            <summary>{pluriel(exclues.length, 'nuit laissée', 'nuits laissées')} hors de la dépense au bar par entrée, et pourquoi</summary>
            <ul className="fsb-exclues__liste">
              {exclues.map((x) => (
                <li key={x.business_date}><span className="fsv-couv__date">{nuitCourte(x.business_date)}</span><span>{x.reason}</span></li>
              ))}
            </ul>
          </details>
        ) : null}
        <p className="lc-hint fsb-note">
          Une entrée est un billet valide (prévente, guichet ou exonération). La guestlist, saisie à la clôture, n’y est pas : cette lecture ne dépend d’aucune clôture.
        </p>
      </div>
    </section>
  )
}
